import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ScrollText, Loader2, ShieldAlert, History, X, ChevronDown } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const ACTIONS = [
  { key: "delete_duplicate", label: "Delete duplicate/test", hint: "Removes an internal record. No money moves, no refund." },
  { key: "reverse", label: "Reverse (offsetting entry)", hint: "Posts a negative entry; keeps the original for history." },
  { key: "write_off", label: "Write off", hint: "Writes off the amount; drops from active reconciliation." },
];

function SplitPill({ label, amount, color }) {
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full ${color}`}>
      {label}: {money(amount)}
    </span>
  );
}

function CorrectModal({ txn, onClose, onDone }) {
  const [action, setAction] = useState("delete_duplicate");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (reason.trim().length < 3) { toast.error("Please enter a reason"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/transactions/${txn.id}/correct`, { action, reason });
      toast.success(data.message || "Corrected");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not correct"); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[120] bg-black/40 grid place-items-center p-4" data-testid="txn-correct-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full p-6">
        <div className="flex items-start justify-between mb-3">
          <h3 className="font-serif text-xl text-[#1A242B]">Correct transaction</h3>
          <button onClick={onClose} data-testid="txn-correct-close"><X size={18} className="text-[#8FA89B]" /></button>
        </div>
        <div className="text-sm text-[#4A5D6B] bg-[#F1EEE8] rounded-lg p-3 mb-4">
          <b>{txn.service_title || txn.ref_kind}</b> · {money(txn.gross)} · {String(txn.created_at || "").slice(0, 10)}
          {/* Split breakdown */}
          <div className="flex flex-wrap gap-1.5 mt-2">
            {txn.ecosystem_share > 0 && <SplitPill label="Ecosystem" amount={txn.ecosystem_share} color="bg-[#D98E73]/20 text-[#B5794E]" />}
            {txn.location_share > 0 && <SplitPill label="Center" amount={txn.location_share} color="bg-[#9E5C7F]/20 text-[#7D4968]" />}
            {txn.enhancer_share > 0 && <SplitPill label="Enhancer" amount={txn.enhancer_share} color="bg-[#5C8A72]/20 text-[#3E6B54]" />}
          </div>
        </div>
        <div className="bg-[#D98E73]/10 border-l-4 border-[#D98E73] p-3 rounded text-xs text-[#B5794E] mb-4 flex gap-2">
          <ShieldAlert size={16} className="shrink-0 mt-0.5" />
          <span>This corrects the <b>internal ledger only</b>. It never issues a refund or moves processor money. To return money to a client, use the POS/Stripe refund flow.</span>
        </div>
        <label className="text-xs text-[#4A5D6B] block mb-1">Correction type</label>
        <div className="space-y-2 mb-4">
          {ACTIONS.map((a) => (
            <label key={a.key} className={`block border rounded-lg p-3 cursor-pointer ${action === a.key ? "border-[#144F75] bg-[#144F75]/5" : "border-[#E5E0D8]"}`} data-testid={`txn-action-${a.key}`}>
              <div className="flex items-center gap-2">
                <input type="radio" checked={action === a.key} onChange={() => setAction(a.key)} />
                <span className="font-medium text-sm text-[#1A242B]">{a.label}</span>
              </div>
              <p className="text-xs text-[#8FA89B] ml-6">{a.hint}</p>
            </label>
          ))}
        </div>
        <label className="text-xs text-[#4A5D6B] block mb-1">Reason (required — recorded in the audit log)</label>
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="txn-reason" rows={2} className="eyw-input w-full mb-4" placeholder="Why is this correction being made?" />
        <div className="flex gap-3">
          <button onClick={onClose} className="eyw-btn-ghost flex-1">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="txn-correct-submit" className="eyw-btn flex-1 disabled:opacity-60">{busy ? <Loader2 size={15} className="animate-spin" /> : "Confirm correction"}</button>
        </div>
      </div>
    </div>
  );
}

export default function TransactionCorrectionPanel() {
  const [q, setQ] = useState("");
  const [correcting, setCorrecting] = useState(null);
  const [showAudit, setShowAudit] = useState(false);
  const txns = useQuery({ queryKey: ["admin-transactions", q], queryFn: () => api.get(`/admin/transactions?q=${q}`).then((r) => r.data) });
  const audit = useQuery({ queryKey: ["admin-audit", showAudit], queryFn: () => api.get("/admin/audit-log?entity=revenue&limit=100").then((r) => r.data), enabled: showAudit });

  const rows = txns.data?.transactions || [];
  return (
    <div className="eyw-card p-5 mt-6" data-testid="txn-correction-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div>
          <h3 className="font-serif text-lg text-[#1A242B] flex items-center gap-2"><ScrollText size={18} className="text-[#144F75]" /> Transaction ledger & corrections</h3>
          <p className="text-xs text-[#8FA89B] mt-0.5">Correct internal records safely — deletion never issues a refund. Every action is logged. Split values are immutable after completion.</p>
        </div>
        <button onClick={() => setShowAudit((s) => !s)} data-testid="txn-toggle-audit" className="eyw-btn-ghost !py-1.5 text-sm"><History size={14} /> {showAudit ? "Hide" : "View"} audit log</button>
      </div>

      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by service or enhancer…" data-testid="txn-search" className="eyw-input w-full mb-3 text-sm" />

      {txns.isLoading ? <div className="text-[#8FA89B] text-sm"><Loader2 size={14} className="animate-spin inline" /> Loading…</div> : (
        <div className="overflow-x-auto max-h-[28rem] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white z-10"><tr className="text-left text-xs text-[#8FA89B] border-b border-[#E5E0D8]">
              <th className="py-2 pr-3">Date</th>
              <th className="py-2 px-2">Type</th>
              <th className="py-2 px-2">Item</th>
              <th className="py-2 px-2 text-right">Gross</th>
              <th className="py-2 px-2 hidden md:table-cell">Revenue Split</th>
              <th className="py-2 px-2"></th>
            </tr></thead>
            <tbody>
              {rows.map((r) => {
                const hasCustomSplit = r.ecosystem_share > 0 || r.location_share > 0;
                return (
                  <tr key={r.id} className={`border-b border-[#F1EEE8] ${r.voided ? "opacity-50" : ""}`} data-testid={`txn-row-${r.id}`}>
                    <td className="py-2 pr-3 text-[#4A5D6B] whitespace-nowrap">{String(r.created_at || "").slice(0, 10)}</td>
                    <td className="py-2 px-2 capitalize text-xs">{r.ref_kind}</td>
                    <td className="py-2 px-2 text-[#1A242B]">{r.service_title || "—"}{r.voided && <span className="ml-2 text-[10px] bg-[#F1EEE8] px-1.5 py-0.5 rounded-full">corrected</span>}</td>
                    <td className="py-2 px-2 text-right font-medium whitespace-nowrap">{money(r.gross)}</td>
                    <td className="py-2 px-2 hidden md:table-cell">
                      {hasCustomSplit ? (
                        <div className="flex flex-wrap gap-1">
                          {r.ecosystem_share > 0 && <SplitPill label="Eco" amount={r.ecosystem_share} color="bg-[#D98E73]/15 text-[#B5794E]" />}
                          {r.location_share > 0 && <SplitPill label="Ctr" amount={r.location_share} color="bg-[#9E5C7F]/15 text-[#7D4968]" />}
                          {r.enhancer_share > 0 && <SplitPill label="Enh" amount={r.enhancer_share} color="bg-[#5C8A72]/15 text-[#3E6B54]" />}
                        </div>
                      ) : (
                        <span className="text-[11px] text-[#C5BFB6] italic">{money(r.enhancer_share)} to enhancer</span>
                      )}
                    </td>
                    <td className="py-2 px-2 text-right">
                      {!r.voided && <button onClick={() => setCorrecting(r)} data-testid={`txn-correct-${r.id}`} className="text-xs text-[#144F75] hover:underline">Correct</button>}
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && <tr><td colSpan={6} className="py-4 text-center text-[#8FA89B]">No transactions.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {showAudit && (
        <div className="mt-4 border-t border-[#E5E0D8] pt-3" data-testid="txn-audit-log">
          <h4 className="font-medium text-sm text-[#1A242B] mb-2">Audit history</h4>
          {(audit.data?.entries || []).length === 0 ? <p className="text-sm text-[#8FA89B]">No corrections recorded.</p> : (
            <ul className="space-y-1.5 text-xs max-h-56 overflow-y-auto">
              {(audit.data?.entries || []).map((e) => (
                <li key={e.id} className="text-[#4A5D6B]"><b>{e.action.replace("transaction.", "")}</b> · {money(e.detail?.gross)} · {e.detail?.reason} — <span className="text-[#8FA89B]">{e.actor_name}, {String(e.created_at).slice(0, 16).replace("T", " ")}</span></li>
              ))}
            </ul>
          )}
        </div>
      )}

      {correcting && <CorrectModal txn={correcting} onClose={() => setCorrecting(null)} onDone={() => { setCorrecting(null); txns.refetch(); if (showAudit) audit.refetch(); }} />}
    </div>
  );
}

